import {
  hasFiltersAppliedSubtitle,
  noFilteredResultsTitle,
} from "@faire/deprecated-retailer-visitor-shared/components/NoResults/index";
import { AnimatedNoResults } from "@faire/deprecated-retailer-visitor-shared/components/SearchProducts/SearchProductsStyles";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSearchPageProductSearchString } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { trackSearchNoResultsInSearchView } from "@faire/web-api--source/events/search/view/noResultsInSearch";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Ref, FunctionComponent, useEffect } from "react";
import styled from "styled-components";

interface INoExactResultsContainerProps {
  onClearFilters?: () => void;
  hasFiltersApplied?: boolean;
  searchPageLoadTimingRef?: Ref<HTMLDivElement>;
}

export const NoExactResultsContainer: FunctionComponent<
  INoExactResultsContainerProps
> = ({
  onClearFilters = () => {},
  hasFiltersApplied,
  searchPageLoadTimingRef,
}) => {
  const query = useSearchPageProductSearchString();

  const strongText = () => {
    if (hasFiltersApplied) {
      return noFilteredResultsTitle;
    }

    if (isLoggedInRetailer()) {
      return (
        <StrictLocalMsg
          id="OixbqW"
          defaultMessage='No results match "{searchToken}"'
          description={{
            text: "This sentence is shown on the product search page when there are no search results.",
            img: "https://cdn.faire.com/fastly/95e8b0e32705e15f011c3a2135b175d5ebb5aea7cebebe5b5a7ba22d93e7f453.png",
          }}
          values={{
            searchToken: query,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        id="zZ7OK3"
        defaultMessage="No results match this exact search"
        description={{
          text: "This sentence is shown on the product search page when there are no search results.",
          img: "https://cdn.faire.com/fastly/846ed47f290d7e4669b7fd2253cb48d25b1d0c5020f4c727e5c2d2e5d8be98da.png",
        }}
      />
    );
  };

  const descriptionText = () => {
    if (hasFiltersApplied) {
      return hasFiltersAppliedSubtitle(onClearFilters);
    }

    return (
      <StrictLocalMsg
        id="kcqnTr"
        defaultMessage="Try searching other keywords."
        description={{
          text: "This sentence is shown on the product search page when there are no search results that match what the user searched for.",
        }}
      />
    );
  };

  useEffect(() => {
    trackSearchNoResultsInSearchView(`${hasFiltersApplied}`);
  }, [hasFiltersApplied]);

  return (
    <MaybeApplyMaxWidthContainer
      $useUpdatedContainerStyling={isLoggedInRetailer()}
    >
      <AnimatedNoResults>
        <div ref={searchPageLoadTimingRef}>
          <Typography
            variant={
              isLoggedInRetailer()
                ? "subheadingSansMedium"
                : "paragraphSansMedium"
            }
            role="heading"
          >
            {strongText()}
          </Typography>
          <Spacer height="1x" />
          <Typography>{descriptionText()}</Typography>
        </div>
      </AnimatedNoResults>
    </MaybeApplyMaxWidthContainer>
  );
};

const MaybeApplyMaxWidthContainer = styled(Row)<{
  $useUpdatedContainerStyling: boolean;
}>`
  max-width: ${({ $useUpdatedContainerStyling }) =>
    $useUpdatedContainerStyling ? "450px" : undefined};
  margin: ${({ $useUpdatedContainerStyling }) =>
    $useUpdatedContainerStyling ? "0 auto" : undefined};
`;
